import { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, AlertOctagon, Briefcase, Scale, LayoutGrid, Rows3 } from "lucide-react";
import { CATEGORIES, DISEASES, INDUSTRY_TABLE, SEVERITY_BG, SEVERITY_LABELS } from "../lib/diseases";
import { DiseaseGrid } from "../components/DiseaseGrid";
import { DiseaseDetailDialog } from "../components/DiseaseDetailDialog";

const SEVERITIES = ["extreme", "high", "warning", "safe"];

export default function OccupationalDiseases() {
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState(null);
  const [activeSev, setActiveSev] = useState(null);
  const [view, setView] = useState("grid"); // "grid" | "list"
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Auto-open a disease when arriving with ?d=<id> (reverse cross-link from element page)
  useEffect(() => {
    const target = searchParams.get("d");
    if (!target) return;
    const match = DISEASES.find((d) => d.id === target);
    if (match) {
      setSelected(match);
      setOpen(true);
      searchParams.delete("d");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const matches = (d) => {
    if (activeCat && d.cat !== activeCat) return false;
    if (activeSev && d.severity !== activeSev) return false;
    if (query) {
      const q = query.toLowerCase();
      const hay = [
        d.name, d.aka || "", d.code, d.cause, d.what,
        (d.industries || []).join(" "), (d.elements || []).join(" "),
      ].join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  };

  const filteredList = useMemo(() => DISEASES.filter(matches), [query, activeCat, activeSev]);

  const handleSelect = (d) => {
    setSelected(d);
    setOpen(true);
  };

  return (
    <div className="bg-white min-h-screen" data-testid="occupational-diseases-page">
      {/* HERO */}
      <section className="border-b-2 border-zinc-950">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
            <div className="md:col-span-8">
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-600 mb-3 flex items-center gap-3">
                <span className="w-8 h-px bg-zinc-950" />
                Reference · India Catalog · Effective 2025
              </div>
              <h1 className="font-heading font-black text-5xl md:text-6xl lg:text-7xl leading-[0.85] tracking-tighter uppercase">
                Occupational<br />
                <span className="bg-zinc-950 text-white px-2 inline-block">Diseases.</span>
              </h1>
              <p className="mt-6 text-lg text-zinc-700 max-w-xl">
                A periodic-table-style catalog of <strong>{DISEASES.length} occupational diseases</strong> across{" "}
                <strong>{CATEGORIES.length} categories</strong>. Click any cell for full details — causes, symptoms,
                diagnosis, treatment, prevention and linked elements.
              </p>
            </div>
            <div className="md:col-span-4">
              <div className="grid grid-cols-2 gap-px bg-zinc-950 border-2 border-zinc-950">
                <Stat n={CATEGORIES.length} l="Categories" />
                <Stat n={DISEASES.length} l="Diseases" />
                <Stat n={INDUSTRY_TABLE.length} l="Industries" />
                <Stat n="2025" l="Catalog" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTROLS */}
      <section className="border-b-2 border-zinc-950 bg-zinc-50 print:hidden">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-5 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
            <div className="md:col-span-4">
              <div className="flex items-center gap-2 border-2 border-zinc-950 bg-white px-3 py-2">
                <Search size={16} strokeWidth={2.5} />
                <input
                  data-testid="disease-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search disease, agent, element, industry…"
                  className="w-full bg-transparent border-0 outline-none font-mono text-sm placeholder:text-zinc-500"
                />
              </div>
            </div>
            <div className="md:col-span-8 flex flex-wrap items-center gap-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600">
                Severity:
              </span>
              {SEVERITIES.map((s) => (
                <button
                  key={s}
                  data-testid={`filter-severity-${s}`}
                  onClick={() => setActiveSev(activeSev === s ? null : s)}
                  className={`${SEVERITY_BG[s]} border-2 border-zinc-950 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest transition-all ${
                    activeSev === s ? "neo-shadow -translate-y-0.5" : ""
                  }`}
                >
                  {SEVERITY_LABELS[s]}
                </button>
              ))}

              <div className="ml-auto flex items-center border-2 border-zinc-950">
                <button
                  onClick={() => setView("grid")}
                  data-testid="view-grid"
                  className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest border-r-2 border-zinc-950 flex items-center gap-1.5 ${
                    view === "grid" ? "bg-zinc-950 text-white" : "bg-white text-zinc-950"
                  }`}
                >
                  <LayoutGrid size={12} strokeWidth={2.5} /> Grid
                </button>
                <button
                  onClick={() => setView("list")}
                  data-testid="view-list"
                  className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest flex items-center gap-1.5 ${
                    view === "list" ? "bg-zinc-950 text-white" : "bg-white text-zinc-950"
                  }`}
                >
                  <Rows3 size={12} strokeWidth={2.5} /> List
                </button>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600">
              Category:
            </span>
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                data-testid={`filter-cat-${c.id}`}
                onClick={() => setActiveCat(activeCat === c.id ? null : c.id)}
                className={`border-2 border-zinc-950 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest transition-all ${
                  activeCat === c.id
                    ? `${c.tone} neo-shadow -translate-y-0.5`
                    : "bg-white text-zinc-950 hover:neo-shadow"
                }`}
                title={c.title}
              >
                {c.n} · {c.short}
              </button>
            ))}
            {(activeCat || activeSev || query) && (
              <button
                data-testid="clear-disease-filters"
                onClick={() => { setActiveCat(null); setActiveSev(null); setQuery(""); }}
                className="font-mono text-[10px] uppercase tracking-widest underline ml-2"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </section>

      {/* GRID / LIST */}
      <section className="bg-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {filteredList.length === 0 ? (
            <div className="font-mono text-sm text-zinc-600 py-12 text-center border-2 border-zinc-950" data-testid="no-results">
              No diseases match your search.
            </div>
          ) : view === "grid" ? (
            <DiseaseGrid diseases={DISEASES} onSelect={handleSelect} highlight={matches} />
          ) : (
            <ListView diseases={filteredList} onSelect={handleSelect} />
          )}
        </div>
      </section>

      {/* INDUSTRY TABLE */}
      <section className="border-t-2 border-zinc-950 bg-zinc-50 print:hidden">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 bg-zinc-950 text-white flex items-center justify-center">
              <Briefcase size={18} strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-600">Summary</div>
              <h2 className="font-heading font-black text-3xl md:text-4xl leading-none tracking-tighter uppercase">
                By Industry
              </h2>
            </div>
          </div>
          <div className="border-2 border-zinc-950 bg-white overflow-x-auto" data-testid="industry-table">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-zinc-950 text-white">
                  <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] w-[200px]">Industry</th>
                  <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em]">Common Industrial Diseases</th>
                </tr>
              </thead>
              <tbody>
                {INDUSTRY_TABLE.map(([ind, dis], i) => (
                  <tr key={ind} className={i % 2 === 0 ? "bg-white" : "bg-zinc-50"}>
                    <td className="px-4 py-3 font-heading font-bold text-base tracking-tight uppercase border-t-2 border-zinc-200">
                      {ind}
                    </td>
                    <td className="px-4 py-3 text-sm text-zinc-700 leading-relaxed border-t-2 border-zinc-200">
                      {dis}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* LEGAL NOTE */}
      <section className="border-t-2 border-zinc-950 bg-white print:hidden">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-12 border-2 border-zinc-950 bg-zinc-950 text-white p-8 grid grid-cols-1 md:grid-cols-12 gap-6">
            <div className="md:col-span-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFD700] mb-3 flex items-center gap-2">
                <Scale size={14} strokeWidth={2.5} /> Important Legal Note
              </div>
              <h2 className="font-heading font-black text-3xl md:text-4xl leading-none tracking-tighter uppercase">
                Compensation Criteria
              </h2>
            </div>
            <div className="md:col-span-8 space-y-3 text-sm leading-relaxed text-zinc-200">
              <p>For compensation purposes (in India and similar systems), a disease is recognized as occupational only if:</p>
              <ol className="space-y-2 list-decimal list-inside marker:text-[#FFD700] marker:font-bold">
                <li>It appears on the official list, <strong className="text-white">and</strong></li>
                <li>It was caused by exposure <strong className="text-white">specifically from work</strong>, <strong className="text-white">and</strong></li>
                <li>The exposure matches the listed hazardous agent for that disease.</li>
              </ol>
              <p className="pt-2 border-t-2 border-zinc-800 mt-3">
                If you suspect an occupational disease, seek evaluation from an{" "}
                <strong className="text-[#FFD700]">occupational medicine physician</strong> — not a
                general doctor — as special diagnostic criteria apply.
              </p>
            </div>
          </div>
          <div className="md:col-span-12 bg-[#FFD700] border-2 border-zinc-950 p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] mb-2 flex items-center gap-1.5">
              <AlertOctagon size={12} strokeWidth={2.75} /> Disclaimer
            </div>
            <p className="text-zinc-950 leading-relaxed">
              This page is a reference and educational tool only. It does not constitute medical or legal
              advice. For an actual exposure or suspected occupational disease, contact your local poison
              control center, emergency services, and a licensed occupational medicine physician immediately.
            </p>
          </div>
        </div>
      </section>

      <DiseaseDetailDialog disease={selected} open={open} onOpenChange={setOpen} />
    </div>
  );
}

const Stat = ({ n, l }) => (
  <div className="bg-white px-4 py-3">
    <div className="font-heading font-black text-3xl leading-none">{n}</div>
    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600 mt-1">{l}</div>
  </div>
);

const ListView = ({ diseases, onSelect }) => {
  // Group by category, in the original 12-category order
  const groups = CATEGORIES
    .map((cat) => ({ cat, items: diseases.filter((d) => d.cat === cat.id) }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="space-y-8" data-testid="disease-list-view">
      {groups.map(({ cat, items }) => {
        const Icon = cat.icon;
        return (
          <div key={cat.id} className="border-2 border-zinc-950" data-testid={`list-cat-${cat.id}`}>
            <div className="flex items-stretch border-b-2 border-zinc-950">
              <div className={`${cat.tone} px-5 py-4 flex items-center gap-3 shrink-0`}>
                <Icon size={20} strokeWidth={2.5} />
                <span className="font-mono text-xs uppercase tracking-[0.25em] font-bold">{cat.n}</span>
              </div>
              <div className="flex-1 px-5 py-4 bg-white">
                <h2 className="font-heading font-black text-2xl md:text-3xl tracking-tighter uppercase leading-none">
                  {cat.title}
                </h2>
                <div className="mt-2 font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-600">
                  Cause · <span className="normal-case tracking-normal text-zinc-700 font-sans">{cat.cause}</span>
                </div>
              </div>
            </div>
            <div className="bg-white">
              {items.map((d, i) => (
                <button
                  key={d.id}
                  data-testid={`list-disease-${d.id}`}
                  onClick={() => onSelect(d)}
                  className={`w-full text-left grid grid-cols-12 gap-3 px-4 py-3 border-t border-zinc-200 hover:bg-zinc-50 transition-colors group ${
                    i === 0 ? "border-t-0" : ""
                  }`}
                >
                  <div className="col-span-2 md:col-span-1 flex items-center">
                    <span className="font-heading font-black text-xl tracking-tight">{d.code}</span>
                  </div>
                  <div className="col-span-10 md:col-span-4">
                    <div className="font-heading font-bold text-base tracking-tight uppercase group-hover:underline">
                      {d.name}
                    </div>
                    {d.aka && (
                      <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">
                        {d.aka}
                      </div>
                    )}
                  </div>
                  <div className="col-span-9 md:col-span-6 text-sm text-zinc-700 leading-snug">
                    {d.what}
                  </div>
                  <div className="col-span-3 md:col-span-1 flex md:justify-end items-center">
                    <span
                      className={`${SEVERITY_BG[d.severity]} border-2 border-zinc-950 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest`}
                    >
                      {SEVERITY_LABELS[d.severity]}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
